import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { formatDate, NgTemplateOutlet, NgClass, DatePipe } from '@angular/common';
import { ExcelService } from "app/services/excel.service";
import { styleType } from "app/components/Controls/NWMTable/NWMTable.component";
import { APIService } from 'app/services/api.service';
import { AuthenticationService, reRgType } from 'app/services/auth.service';
import { LoaderService } from "app/services/loader.service";
import { AppConfig } from "app/app.config";
import { MenuComponent } from '../../components/Controls/menu/menu.component';
import { VDPickerComponent } from '../../components/Controls/vd-picker/vd-picker.component';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { RouterLink } from '@angular/router';
import { MinusSignToParens } from 'app/pipes/minus-sign-to-parens.pipe';
import { PlaceholderPipe } from 'app/pipes/placeholder.pipe';


type apiTotalBlended = {
  CurrentMarketValue: string;
  CurrentMarketValue_Including_Other: string;
  PerformanceStartDate: string;
  AnnualizedReturnSinceInception: string;
  BeginningOfYearBalance: string;
  DepositsYTD: string;
  GainLossForTheYear: string;
  GainLossSinceInception: string;
  NetDepositsWithdrawalsYTD: string;
  WithdrawalsYTD: string;
  YearToDateReturn: string;
};

type apiFirstLevelDetailsType = {
  isActive: number;
  Owner: string;
  Plan: {
    isActive: number;
    isLessThanYear: boolean;
    Key: string;
    SUM_CurrentMarketValue: string;
    Total_MIN_PerformanceStartDate: string;
    Total_SUM_AnnualizedReturnSinceInception: string;
    Total_SUM_BeginningOfYearBalance: string;
    Total_SUM_CurrentMarketValue: string;
    Total_SUM_DepositsYTD: string;
    Total_SUM_GainLossForTheYear: string;
    Total_SUM_GainLossSinceInception: string;
    Total_SUM_NetDepositsWithdrawalsYTD: string;
    Total_SUM_WithdrawalsYTD: string;
    Total_SUM_YearToDateReturn: string;
  }[];
};

type apiTableInformationTotalType = {
  AnnualizedReturnSinceInception: string;
  BeginningOfYearBalance: string;
  CurrentMarketValue: string;
  DepositsYTD: string;
  GainLossForTheYear: string;
  GainLossSinceInception: string;
  isLessThanYear: boolean;
  NetDepositsWithdrawalsYTD: string;
  PerformanceStartDate: string;
  title: string;
  WithdrawalsYTD: string;
  YearToDateReturn: string;
};

type apiOtherHoldingType = {
  isActive: number;
  Owner: string;
  Plan: {
    isActive: number;
    Key: string;
    SUM_CurrentMarketValue: string;
  }[];
};

type apiTotalUsdInCadType = {
  AnnualizedReturnSinceInception: string;
  BeginningOfYearBalance: string;
  CurrentMarketValue: string;
  DepositsYTD: string;
  Excluded_Market_Value: string;
  GainLossForTheYear: string;
  GainLossSinceInception: string;
  NetDepositsWithdrawalsYTD: string;
  PerformanceStartDate: string;
  WithdrawalsYTD: string;
  YearToDateReturn: string;
};

type apiTotalType = {
  CurrentMarketValue: string;
  Excluded_Market_Value: string;
  PerformanceStartDate: string;
};

type apiDataType = {
  TableInformation: {
    Currency: string;
    FirstLevelDetails: apiFirstLevelDetailsType[];
    OtherHoldings: apiOtherHoldingType[];
    Total: apiTableInformationTotalType[];
  }[];
  TotalBlended: apiTotalBlended[];
  TotalCad: apiTotalType[];
  TotalUsd: apiTotalType[];
  TotalUsdInCad: apiTotalUsdInCadType[];
};

type dataTotalType = {
  AnnualizedReturnSinceInception?: string;
  BeginningOfYearBalance?: string;
  CurrentMarketValue?: string;
  DepositsYTD?: string;
  GainLossForTheYear?: string;
  GainLossSinceInception?: string;
  grand_total?: boolean;
  isLessThanYear: boolean;
  NetDepositsWithdrawalsYTD?: string;
  PerformanceStartDate?: string;
  title: string;
  WithdrawalsYTD?: string;
  YearToDateReturn?: string;
};

type dataAccountType = {
  Owner: string;
  Plan: {
    isActive: number;
    isLessThanYear?: boolean;
    Key: string;
    SUM_CurrentMarketValue: string;
    Total_MIN_PerformanceStartDate?: string;
    Total_SUM_AnnualizedReturnSinceInception?: string;
    Total_SUM_BeginningOfYearBalance?: string;
    Total_SUM_CurrentMarketValue?: string;
    Total_SUM_DepositsYTD?: string;
    Total_SUM_GainLossForTheYear?: string;
    Total_SUM_GainLossSinceInception?: string;
    Total_SUM_NetDepositsWithdrawalsYTD?: string;
    Total_SUM_WithdrawalsYTD?: string;
    Total_SUM_YearToDateReturn?: string;
  }[];
};

type dataType = {
  accounts: dataAccountType[];
  currency: string;
  isLessThanYear?: boolean;
  opened?: boolean;
  other?: boolean;
  totals: dataTotalType[];
};


@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './portfolio.component.html',
    styleUrls: ['./portfolio.component.scss'],
    imports: [MenuComponent, VDPickerComponent, NgTemplateOutlet, NgClass, NgbTooltip, RouterLink, MinusSignToParens, DatePipe, PlaceholderPipe]
})

export class PortfolioComponent implements OnInit {
  private api = inject(APIService);
  private auth = inject(AuthenticationService);
  private loaderService = inject(LoaderService);
  private config = inject(AppConfig);
  private excel = inject(ExcelService);
  private cdr = inject(ChangeDetectorRef);

  public valuationDate  = '';
  public data: dataType[] = [];
  public vd?: Date;
  public showDWdetails = false; //being used in html to show/hide extra columns
  public grandTotal = '';
  public isPershing = false;
  public selectedReRg?: reRgType;

  ngOnInit(): void {
    this.selectedReRg = this.auth.getCurrentReRg()!;
    this.loadPortfolioPageData();
  }

  onValuationDateChange() {
    this.loadPortfolioPageData();
  }

  loadPortfolioPageData() {
    this.valuationDate = this.auth.getCurrentValuationDate();
    if (!this.valuationDate) return;
    this.loaderService.display(true, 'PortfolioTotals');
    this.isPershing = this.selectedReRg!.IsPershing;
    this.vd = this.valuationDate!.toNWMdate();
    this.api.get<apiDataType>(`${this.config.portfolio.portfolioDetailsTotalsUrl}/${this.selectedReRg!.ReRgId}/${this.valuationDate}`).subscribe(
      data => {
        const info: dataType[] = [];
        if (data && data.TableInformation && data.TableInformation.length) {
          data.TableInformation.forEach(t => {
            const filtered = t.FirstLevelDetails.filter(a => a.isActive !== 0);
            if (filtered.length) {
              const accounts: dataAccountType[] = filtered.map(f => ({
                Plan: f.Plan.map(p => ({
                  isActive: p.isActive,
                  isLessThanYear: this.isLessThanYear(p.Total_MIN_PerformanceStartDate),
                  Key: p.Key,
                  SUM_CurrentMarketValue: p.SUM_CurrentMarketValue,
                  Total_MIN_PerformanceStartDate: p.Total_MIN_PerformanceStartDate,
                  Total_SUM_AnnualizedReturnSinceInception: p.Total_SUM_AnnualizedReturnSinceInception,
                  Total_SUM_BeginningOfYearBalance: p.Total_SUM_BeginningOfYearBalance,
                  Total_SUM_DepositsYTD: p.Total_SUM_DepositsYTD,
                  Total_SUM_GainLossForTheYear: p.Total_SUM_GainLossForTheYear,
                  Total_SUM_CurrentMarketValue: p.Total_SUM_CurrentMarketValue,
                  Total_SUM_GainLossSinceInception: p.Total_SUM_GainLossSinceInception,
                  Total_SUM_NetDepositsWithdrawalsYTD: p.Total_SUM_NetDepositsWithdrawalsYTD,
                  Total_SUM_YearToDateReturn: p.Total_SUM_YearToDateReturn,
                  Total_SUM_WithdrawalsYTD: p.Total_SUM_WithdrawalsYTD,
                })),
                Owner: f.Owner,
              }));

              const totals: dataTotalType[] = [];
              const firstTotal = t.Total[0];
              totals.push({
                AnnualizedReturnSinceInception: firstTotal.AnnualizedReturnSinceInception,
                BeginningOfYearBalance: firstTotal.BeginningOfYearBalance,
                CurrentMarketValue: firstTotal.CurrentMarketValue,
                DepositsYTD: firstTotal.DepositsYTD,
                GainLossForTheYear: firstTotal.GainLossForTheYear,
                GainLossSinceInception: firstTotal.GainLossSinceInception,
                isLessThanYear: false,
                NetDepositsWithdrawalsYTD: firstTotal.NetDepositsWithdrawalsYTD,
                PerformanceStartDate: firstTotal.PerformanceStartDate,
                title: 'Total ' + (t.Currency == 'USD' ? 'US' : 'Canadian') + ' Dollar Portfolio',
                WithdrawalsYTD: firstTotal.WithdrawalsYTD,
                YearToDateReturn: firstTotal.YearToDateReturn,
              });
              if (t.Currency == 'USD' && !this.isPershing) {
                const totalUsdInCad: dataTotalType = {
                  isLessThanYear: false,
                  title: 'Canadian Dollar Equivalent',
                };
                if (data.TotalUsdInCad && data.TotalUsdInCad.length > 0) {
                  const firstTotalUsdInCad = data.TotalUsdInCad[0];
                  totalUsdInCad.AnnualizedReturnSinceInception = firstTotalUsdInCad.AnnualizedReturnSinceInception;
                  totalUsdInCad.BeginningOfYearBalance = firstTotalUsdInCad.BeginningOfYearBalance;
                  totalUsdInCad.CurrentMarketValue = firstTotalUsdInCad.CurrentMarketValue;
                  totalUsdInCad.DepositsYTD = firstTotalUsdInCad.DepositsYTD;
                  totalUsdInCad.GainLossForTheYear = firstTotalUsdInCad.GainLossForTheYear;
                  totalUsdInCad.GainLossSinceInception = firstTotalUsdInCad.GainLossSinceInception;
                  totalUsdInCad.NetDepositsWithdrawalsYTD = firstTotalUsdInCad.NetDepositsWithdrawalsYTD;
                  totalUsdInCad.PerformanceStartDate = firstTotalUsdInCad.PerformanceStartDate;
                  totalUsdInCad.WithdrawalsYTD = firstTotalUsdInCad.WithdrawalsYTD;
                  totalUsdInCad.YearToDateReturn = firstTotalUsdInCad.YearToDateReturn;
                }
                totals.push(totalUsdInCad);
              }
              info.push({currency: t.Currency, accounts: accounts, totals: totals, opened: true, isLessThanYear: this.isLessThanYear(totals[0].PerformanceStartDate)});
            }
          });

          if (!this.isPershing) {
            this.grandTotal = data.TotalBlended[0].CurrentMarketValue;
            const firstTotalBlended = data.TotalBlended[0];
            info[info.length - 1].totals.push({
              AnnualizedReturnSinceInception: firstTotalBlended.AnnualizedReturnSinceInception,
              BeginningOfYearBalance: firstTotalBlended.BeginningOfYearBalance,
              CurrentMarketValue: firstTotalBlended.CurrentMarketValue,
              DepositsYTD: firstTotalBlended.DepositsYTD,
              GainLossForTheYear: firstTotalBlended.GainLossForTheYear,
              GainLossSinceInception: firstTotalBlended.GainLossSinceInception,
              grand_total: true,
              isLessThanYear: false,
              NetDepositsWithdrawalsYTD: firstTotalBlended.NetDepositsWithdrawalsYTD,
              PerformanceStartDate: firstTotalBlended.PerformanceStartDate,
              title: 'TOTAL PORTFOLIO (CAD)',
              WithdrawalsYTD: firstTotalBlended.WithdrawalsYTD,
              YearToDateReturn: firstTotalBlended.YearToDateReturn,
            });
          }

          let hasOther = false;
          data.TableInformation.forEach(t => {
            if (t.OtherHoldings && t.OtherHoldings.length) {
              const filtered = t.OtherHoldings.filter(a => a.isActive !== 0);
              if (filtered.length) {
                const accounts: dataAccountType[] = filtered.map(f => ({
                  Plan: f.Plan.map(p => ({
                    isActive: p.isActive,
                    Key: p.Key,
                    SUM_CurrentMarketValue: p.SUM_CurrentMarketValue,
                  })),
                  Owner: f.Owner,
                }));
                hasOther = true;
                const totals: dataTotalType[] = [];
                if (t.Currency == 'USD') {
                  totals.push({ CurrentMarketValue: data.TotalUsd[0].Excluded_Market_Value, isLessThanYear: false, title: 'Total US Dollar Portfolio' });
                  if(!this.isPershing)
                    totals.push({CurrentMarketValue: data.TotalUsdInCad[0].Excluded_Market_Value, isLessThanYear: false, title : 'Canadian Dollar Equivalent'});
                } else {
                  totals.push({CurrentMarketValue: data.TotalCad[0].Excluded_Market_Value, isLessThanYear: false, title: 'Total Canadian Dollar Portfolio'});
                }
                info.push({
                  currency: t.Currency,
                  other: true,
                  accounts: accounts,
                  totals: totals,
                });
              }
            }
          });
          if (hasOther && !this.isPershing) {
            info[info.length-1].totals.push({
              CurrentMarketValue: data.TotalBlended[0].CurrentMarketValue_Including_Other,
              grand_total: true,
              isLessThanYear: false,
              title: 'TOTAL PORTFOLIO (CAD)',
            });
          }
          info.forEach(i=>{i.totals.forEach(t => {t.isLessThanYear=this.isLessThanYear(t.PerformanceStartDate) }) });
        }
        this.data = info;
        setTimeout(()=>{this.cdr.detectChanges();});
        this.loaderService.display(false, 'PortfolioTotals');
      },
      () => {
        setTimeout(() => this.cdr.detectChanges());
        this.loaderService.display(false, 'PortfolioTotals');
      }
    );
  }

  isLessThanYear(d: unknown) {
    if (typeof d !== 'string') return false;
    const d2 = d.toNWMdate();
    return ((+this.vd! - +d2) / 1000 / 60 / 60 / 24) <= 365;
  }

  DWClick() {
    this.showDWdetails=!this.showDWdetails;
    setTimeout(()=>{$(window).trigger('scroll');},0);
  }

  export() {
    const headers = [ "Owner", "Account Type", "Beginning of Year\r\nMarket Value ($)", "Deposits\r\nYTD ($)", "Withdrawals\r\nYTD ($)", "Net Deposits/\r\nWithdrawals YTD ($)",
      "Current Market\r\nValue ($)", "Gain/Loss for\r\nthe Year ($)", "YTD Return", "Gain/Loss Since\r\nInception ($)",
      "Annualized Return\r\nSince Inception *", "Performance\r\nStart Date"];
    const lastCol = headers.length-1;

    const rows = [
      [(this.selectedReRg!.ClientName || '') + ' - Summary of Accounts','','','','','','','','','','',this.excel.toExcelLocalTime(new Date())],
      ['Portfolio Details as of ' + formatDate(this.valuationDate!, 'MMM d, yyyy', 'en-US')],
      [],
      headers,
    ];
    const styles: styleType[] = [
      // styles can be applied to row, col or cell, but if you do it more then once, only the latest will apply
      {col:[0,1], font: "Arial 10 #000000 B", align: 'L C', width:20}, // make first two columns bold
      {col:'2-'+lastCol, align: 'R C', width:20}, // right-center align numeric columns
      {row:0, font: "14"},
      {row:1, font: "12"},
      {row:3, height:30},
      {row:0, col:lastCol, format: "m-d-yyyy h:mm:ss AM/PM", font:'8'},
      {row:3, col:'0-'+lastCol, fill: "#183750", font: "Arial 10 #ffffff B", wrapText:1},
    ];

    const titleStyles: styleType = {row:[], col:'0-'+lastCol, fill: "#183750", font: "Arial 10 #ffffff B"};

    this.data.forEach(t => {
      rows.push([ (t.other ? 'OTHER HOLDINGS ' : '')+(t.currency == 'USD' ? 'US ' : 'CANADIAN ') + 'DOLLAR ACCOUNTS']);
      (titleStyles.row as number[]).push(rows.length-1); //highlight title row
      t.accounts.forEach(account => {
        rows.push([account.Owner]);
        account.Plan.forEach(plan => {
          if (plan.isActive !== 0) {
            rows.push(['',plan.Key,
              this.unformat(plan.Total_SUM_BeginningOfYearBalance),
              this.unformat(plan.Total_SUM_DepositsYTD),
              this.unformat(plan.Total_SUM_WithdrawalsYTD),
              this.unformat(plan.Total_SUM_NetDepositsWithdrawalsYTD),
              this.unformat(t.other ? plan.SUM_CurrentMarketValue : plan.Total_SUM_CurrentMarketValue),
              this.unformat(plan.Total_SUM_GainLossForTheYear),
              this.unformat(plan.Total_SUM_YearToDateReturn),
              this.unformat(plan.Total_SUM_GainLossSinceInception),
              this.unformat(plan.Total_SUM_AnnualizedReturnSinceInception) + (plan.isLessThanYear ? '*':''),
              plan.Total_MIN_PerformanceStartDate
            ]);
            styles.push({row: rows.length-1,col:['2-7',9], format: '#,##0.00;(#,##0.00);&quot;-&quot;'});
            styles.push({row: rows.length-1,col:[8,10], format: '0.00%;(0.00%);&quot;-&quot;'});
          }
        })
      });
      t.totals.forEach(total => {
        rows.push([total.title, '',
          this.unformat(total.BeginningOfYearBalance),
          this.unformat(total.DepositsYTD),
          this.unformat(total.WithdrawalsYTD),
          this.unformat(total.NetDepositsWithdrawalsYTD),
          this.unformat(total.CurrentMarketValue),
          this.unformat(total.GainLossForTheYear),
          this.unformat(total.YearToDateReturn),
          this.unformat(total.GainLossSinceInception),
          this.unformat(total.AnnualizedReturnSinceInception) + (total.isLessThanYear ? '*':''),
          total.PerformanceStartDate
        ]);
        const color = total.grand_total ? '#183750':'#d1d7dc';
        const font = total.grand_total ? 'Arial 10 #ffffff B':'Arial 10 #000000 B';
        styles.push({row: rows.length-1,col:[2,3,4,5,6,7,9], format: '$#,##0.00;(#,##0.00);&quot;-&quot;', fill: color, font: font, border: "none,none,none,thin #ffffff"});
        styles.push({row: rows.length-1,col:[8,10], format: '0.00%;(0.00%);&quot;-&quot;', fill: color, font: font, border: "none,none,none,thin #ffffff"});
        styles.push({row: rows.length-1,col:[0,1,lastCol], fill: color, font: font, border: "none,none,none,thin #ffffff"});
      });
    });
    styles.push(titleStyles);

    rows.push([],[],
      ['E. & O.E. for illustration purposes only. Past performance is not necessarily indicative of future performance. Please refer to the FAQ section and your Nicola Wealth Portfolio Statements located in the Documents section of your Nicola Wealth Private Client Access Website for additional disclosures.'],
      ['* If the inception date of your account is less than one (1) year, the "Annualized Return Since Inception" displayed is calculated as a cumulative return from inception.']
    );
    styles.push({row:[rows.length-2,rows.length-1], font: "8"});

    this.excel.generate('PortfolioSummary.xlsx', [{sheetTitle:'Portfolio Summary Accounts', rows:rows, styles:styles}]);
  }

  unformat(a: unknown) {
    if (a === undefined) return '';
    if (typeof a != 'string' && typeof a != 'number') return a;
    const string = a.toString();
    let v = parseFloat(string.replace(/[^0-9.]*/g,''));
    if (string.indexOf('%') > -1) v = v / 100;
    return string.indexOf('(') > -1 || string.indexOf('-') > -1 ? '-' + v : v;
  }
}